import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PawPrint } from "lucide-react";

export default function RegisterPage() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isBreeder, setIsBreeder] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await register(email, password, name, isBreeder);
    setSubmitting(false);
    if (ok) nav("/welcome");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md border-border shadow-none">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <PawPrint size={18} />
            </div>
            <span className="font-display font-bold text-lg">Your Dog Days</span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Get started</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-8">Create your account</h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required minLength={6} />
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none rounded-md border border-border p-3 hover:bg-muted/40 transition-colors" data-testid="register-breeder-toggle">
              <input
                type="checkbox"
                checked={isBreeder}
                onChange={(e) => setIsBreeder(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                data-testid="register-breeder-checkbox"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground block">I&apos;m a breeder</span>
                <span className="text-muted-foreground text-xs">Unlocks the gestation calendar, litter dashboard, ENS protocol, puppy weights and socialization tracker. You can change this anytime in Settings.</span>
              </span>
            </label>
            {error ? <div data-testid="register-error" className="text-sm text-destructive">{error}</div> : null}
            <Button type="submit" data-testid="register-submit-button" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all">
              {submitting ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="underline underline-offset-4 text-foreground" data-testid="goto-login-link">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
